import { useMutation, useQuery } from '@tanstack/react-query'
import { ArrowLeft, ArrowRight, Check, Loader2, Lock, RefreshCw, X } from 'lucide-react'
import { observer } from 'mobx-react'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router'

import { Button } from '@/components/elements/button'
import { Layout } from '@/components/Layout'
import { getEAP, getEAPQuiz, getEAPQuizStatus, submitEAPQuiz } from '@/services/eap.service'
import { cn, toast } from '@/utils/helpers'

const formatDate = (date) =>
  date
    ? new Date(date).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : '—'

export const EAPQuiz = observer(() => {
  const { entityId, eapId } = useParams()
  const navigate = useNavigate()

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0)
  const [responses, setResponses] = useState({})
  const [quizResult, setQuizResult] = useState(null)

  // GET EAP details
  const {
    data: eapData,
    isPending: isLoadingEAP,
    error: eapError,
  } = useQuery({
    queryKey: ['accounts', 'eaps', entityId, eapId],
    queryFn: () => getEAP({ entityId, eapId }),
  })

  // GET Quiz questions
  const {
    data: quizQuestions,
    isPending: isLoadingQuiz,
    error: quizError,
  } = useQuery({
    queryKey: ['accounts', 'eaps', entityId, eapId, 'quiz'],
    queryFn: () => getEAPQuiz({ entityId, eapId }),
  })

  // GET Quiz status (open window, due date) — used to gate the quiz before
  // it's open, so the user gets a clear "opens on X" screen rather than a
  // generic 403 when they try to submit.
  const {
    data: quizStatus,
    isPending: isLoadingQuizStatus,
    error: quizStatusError,
  } = useQuery({
    queryKey: ['accounts', 'eaps', entityId, eapId, 'quiz_status'],
    queryFn: () => getEAPQuizStatus({ entityId, eapId }),
  })

  // Submit quiz
  const { isPending: isSubmitting, mutate: submitQuizMutation } = useMutation({
    mutationFn: (data) => submitEAPQuiz({ entityId, eapId, responses: data }),
    onError: (err) => toast(err.message, 'error'),
    onSuccess: (data) => {
      setQuizResult(data)
    },
  })

  const handleSelectAnswer = (questionId, answerText) => {
    setResponses((prev) => ({
      ...prev,
      [questionId]: answerText,
    }))
  }

  const handleNext = () => {
    if (quizQuestions && currentQuestionIndex < quizQuestions.length - 1) {
      setCurrentQuestionIndex((prev) => prev + 1)
    }
  }

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex((prev) => prev - 1)
    }
  }

  const handleSubmit = () => {
    const formattedResponses = Object.entries(responses).map(([questionId, selectedAnswer]) => ({
      questionId,
      selectedAnswer,
    }))
    submitQuizMutation(formattedResponses)
  }

  const handleRetry = () => {
    setCurrentQuestionIndex(0)
    setResponses({})
    setQuizResult(null)
  }

  const handleBackToEAPs = () => {
    navigate(`/accounts/${entityId}/eaps`)
  }

  if (isLoadingEAP || isLoadingQuiz || isLoadingQuizStatus) {
    return (
      <Layout
        breadcrumbs={[
          'Admin',
          { pathname: `/accounts/${entityId}/eaps`, title: 'Emergency Action Plans' },
          'Quiz',
        ]}
      >
        <div className="flex items-center justify-center py-12">
          <Loader2 className="text-muted-foreground h-8 w-8 animate-spin" />
        </div>
      </Layout>
    )
  }

  if (eapError) {
    return (
      <Layout
        breadcrumbs={[
          'Admin',
          { pathname: `/accounts/${entityId}/eaps`, title: 'Emergency Action Plans' },
          'Quiz',
        ]}
      >
        <div className="flex flex-col items-center justify-center py-12">
          <p className="mb-4 text-gray-600">Unable to load EAP. Please try again.</p>
          <Button onClick={handleBackToEAPs}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to EAPs
          </Button>
        </div>
      </Layout>
    )
  }

  if (quizError || quizStatusError) {
    // Distinguish "no quiz here" (404) from genuine load failures so users
    // don't see a confusing error when they simply have no questions. DRF
    // returns "Not found." (capitalized) — match case-insensitively.
    const err = quizError || quizStatusError
    const message = err?.message?.toLowerCase() || ''
    const is404 = message.includes('404') || message.includes('not found')
    return (
      <Layout
        breadcrumbs={[
          'Admin',
          { pathname: `/accounts/${entityId}/eaps`, title: 'Emergency Action Plans' },
          'Quiz',
        ]}
      >
        <div className="flex flex-col items-center justify-center py-12">
          <p className="mb-4 text-gray-600">
            {is404 ? 'No quiz available for this EAP.' : 'Unable to load quiz. Please try again.'}
          </p>
          <Button onClick={handleBackToEAPs}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to EAPs
          </Button>
        </div>
      </Layout>
    )
  }

  // Quiz is locked until 30 days before the next due date.
  if (quizStatus && quizStatus.hasQuiz && !quizStatus.isOpen) {
    return (
      <Layout
        breadcrumbs={[
          'Admin',
          { pathname: `/accounts/${entityId}/eaps`, title: 'Emergency Action Plans' },
          eapData?.name || 'Quiz',
        ]}
      >
        <div className="mx-auto max-w-2xl">
          <div className="rounded-lg border border-gray-200 bg-white p-8 text-center">
            <Lock className="mx-auto mb-4 h-10 w-10 text-gray-400" />
            <h1 className="mb-2 text-2xl font-semibold text-gray-900">Quiz not yet available</h1>
            <p className="mb-2 text-gray-600">
              You can take this quiz starting <strong>{formatDate(quizStatus.openAt)}</strong> (30
              days before it&apos;s due).
            </p>
            <p className="mb-6 text-sm text-gray-500">
              Next due date: {formatDate(quizStatus.nextDueDate)}
            </p>
            <Button onClick={handleBackToEAPs}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to EAPs
            </Button>
          </div>
        </div>
      </Layout>
    )
  }

  // Guard check for empty quiz questions
  if (!quizQuestions || quizQuestions.length === 0) {
    return (
      <Layout
        breadcrumbs={[
          'Admin',
          { pathname: `/accounts/${entityId}/eaps`, title: 'Emergency Action Plans' },
          eapData?.name || 'Quiz',
        ]}
      >
        <div className="mx-auto max-w-2xl">
          <div className="rounded-lg border border-gray-200 bg-white p-8 text-center">
            <h1 className="mb-2 text-2xl font-semibold text-gray-900">
              No quiz questions available
            </h1>
            <p className="mb-6 text-gray-600">
              There are currently no questions configured for this quiz.
            </p>
            <Button onClick={handleBackToEAPs}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to EAPs
            </Button>
          </div>
        </div>
      </Layout>
    )
  }

  // Quiz result screen
  if (quizResult) {
    const passed = quizResult.result === 'passed'
    const totalQuestions = quizResult.totalQuestions || 0
    const percentage =
      totalQuestions > 0 ? Math.round((quizResult.score / totalQuestions) * 100) : 0

    return (
      <Layout
        breadcrumbs={[
          'Admin',
          { pathname: `/accounts/${entityId}/eaps`, title: 'Emergency Action Plans' },
          'Quiz Result',
        ]}
      >
        <div className="mx-auto max-w-2xl">
          <div className="rounded-lg border border-gray-200 bg-white p-8 text-center">
            <div
              className={cn(
                'mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full',
                passed ? 'bg-green-100' : 'bg-red-100',
              )}
            >
              {passed ? (
                <Check className="h-8 w-8 text-green-600" />
              ) : (
                <X className="h-8 w-8 text-red-600" />
              )}
            </div>

            <h1 className="mb-2 text-2xl font-semibold text-gray-900">
              {passed ? 'Congratulations!' : 'Quiz Not Passed'}
            </h1>

            <p className="mb-6 text-gray-600">
              {passed
                ? 'You have successfully passed the EAP quiz.'
                : 'You did not reach the required score. Please review the EAP and try again.'}
            </p>

            <div className="mb-6">
              <div className="text-4xl font-bold text-gray-900">{percentage}%</div>
              <div className="text-sm text-gray-500">
                {quizResult.score} of {totalQuestions} correct
              </div>
              <div className="mt-2 text-sm text-gray-500">(80% required to pass)</div>
            </div>

            <div className="flex justify-center gap-3">
              {!passed && (
                <Button onClick={handleRetry} variant="outline">
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Try Again
                </Button>
              )}
              <Button onClick={handleBackToEAPs}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to EAPs
              </Button>
            </div>
          </div>
        </div>
      </Layout>
    )
  }

  const currentQuestion = quizQuestions[currentQuestionIndex]
  const selectedAnswer = responses[currentQuestion.id]
  const isLastQuestion = currentQuestionIndex === quizQuestions.length - 1
  const answeredCount = Object.keys(responses).length
  const allAnswered = answeredCount === quizQuestions.length

  return (
    <Layout
      breadcrumbs={[
        'Admin',
        { pathname: `/accounts/${entityId}/eaps`, title: 'Emergency Action Plans' },
        eapData?.name || 'Quiz',
      ]}
    >
      <div className="mx-auto max-w-2xl">
        {/* Header */}
        <div className="mb-6 flex items-center gap-3">
          <Button variant="ghost" onClick={handleBackToEAPs}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">{eapData?.name} - Quiz</h1>
            <p className="text-sm text-gray-500">
              Question {currentQuestionIndex + 1} of {quizQuestions.length}
            </p>
          </div>
        </div>

        {/* Progress bar */}
        <div
          className="mb-6 h-2 overflow-hidden rounded-full bg-gray-200"
          role="progressbar"
          aria-valuenow={currentQuestionIndex + 1}
          aria-valuemin={1}
          aria-valuemax={quizQuestions.length}
          aria-label={`Quiz progress: Question ${currentQuestionIndex + 1} of ${quizQuestions.length}`}
        >
          <div
            className="h-full bg-blue-600 transition-all duration-300"
            style={{ width: `${((currentQuestionIndex + 1) / quizQuestions.length) * 100}%` }}
          />
        </div>

        {/* Question card */}
        <div className="rounded-lg border border-gray-200 bg-white p-6">
          <p className="mb-2 text-xs text-gray-500">
            Related to: {currentQuestion.eapQuestionLabel}
          </p>
          <h2 className="mb-6 text-lg font-medium text-gray-900">{currentQuestion.text}</h2>

          {/* Answer options */}
          <div className="space-y-3">
            {currentQuestion.answers.map((answer, idx) => {
              const letter = String.fromCharCode(65 + idx)
              return (
                <button
                  type="button"
                  // eslint-disable-next-line react/no-array-index-key
                  key={`${currentQuestion.id}-answer-${idx}`}
                  onClick={() => handleSelectAnswer(currentQuestion.id, answer.text)}
                  className={cn(
                    'w-full rounded-lg border p-4 text-left transition-colors',
                    selectedAnswer === answer.text
                      ? 'border-blue-500 bg-blue-50 text-blue-900'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50',
                  )}
                >
                  <span className="mr-3 inline-flex h-6 w-6 items-center justify-center rounded-full border border-current text-sm font-medium">
                    {letter}
                  </span>
                  {answer.text}
                </button>
              )
            })}
          </div>
        </div>

        {/* Navigation */}
        <div className="mt-6 flex justify-between">
          <Button variant="outline" onClick={handlePrevious} disabled={currentQuestionIndex === 0}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Previous
          </Button>

          {isLastQuestion ? (
            <Button onClick={handleSubmit} disabled={!allAnswered || isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Check className="mr-2 h-4 w-4" />
                  Submit Quiz
                </>
              )}
            </Button>
          ) : (
            <Button onClick={handleNext} disabled={!selectedAnswer}>
              Next
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          )}
        </div>

        {/* Question indicators */}
        <div className="mt-6 flex justify-center gap-2">
          {quizQuestions.map((q, idx) => {
            const getIndicatorColor = () => {
              if (idx === currentQuestionIndex) return 'bg-blue-600'
              if (responses[q.id]) return 'bg-green-500'
              return 'bg-gray-300'
            }
            return (
              <button
                type="button"
                key={q.id}
                onClick={() => setCurrentQuestionIndex(idx)}
                className={cn('h-2 w-2 rounded-full transition-colors', getIndicatorColor())}
                aria-label={`Go to question ${idx + 1}`}
              />
            )
          })}
        </div>
      </div>
    </Layout>
  )
})
